import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface LogEntry {
    ModifiedBy: string;
    ModifiedDate: string;
    Activity: string;
}

const styles = {
    pageContainer: {
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        padding: '10px',
        backgroundColor: 'White',
        minHeight: '100vh',
        boxSizing: 'border-box' as const
    },
    messageLabel: {
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: '10px',
        display: 'block'
    },
    gridContainer: {
        width: '100%',
        maxHeight: 'calc(100vh - 40px)',
        overflow: 'auto',
        border: '1px solid #cccccc',
        backgroundColor: 'White'
    },
    table: {
        width: '100%',
        tableLayout: 'fixed' as const,
        borderCollapse: 'collapse' as const,
        textAlign: 'left' as const,
        color: '#333333'
    },
    th: {
        backgroundColor: '#5D7B9D',
        color: 'White',
        fontWeight: 'bold',
        padding: '6px 4px',
        border: '1px solid White',
        position: 'sticky' as const,
        top: 0,
        zIndex: 1
    },
    td: {
        padding: '4px',
        borderBottom: '1px solid #cccccc',
        verticalAlign: 'middle' as const,
        wordWrap: 'break-word' as const
    },
    rowNormal: {
        backgroundColor: '#F7F6F3',
        color: '#333333'
    },
    rowAlternate: {
        backgroundColor: 'White',
        color: '#284775'
    }
};

const WLPModifiedLog: React.FC = () => {
    const [searchParams] = useSearchParams();
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<string>('');

    const schoolCode = searchParams.get('SchoolCode') || '';
    const familyCode = searchParams.get('FamilyCode') || '';
    const login = searchParams.get('Login') || '';
    const subject = searchParams.get('Subject') || '';

    const currentUserName = sessionStorage.getItem('UserName') || 'System';

    useEffect(() => {
        if (schoolCode && familyCode && login && subject) {
            fetchLogs();
        } else {
            setMessage('Missing required parameters.');
            setLoading(false);
        }
    }, [schoolCode, familyCode, login, subject]);

    const fetchLogs = async () => {
        setLoading(true);
        setMessage('');
        
        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                FamilyCode: familyCode,
                Login: login,
                Subject: subject,
                CurrentUser: currentUserName
            });

            const response = await fetch(`${API_BASE_URL}api/wlp/modified_log/?${params.toString()}`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch logs');
            }

            const data = await response.json();
            
            if (data.error) {
                setMessage(data.error);
            } else if (data.logs && data.logs.length > 0) {
                setLogs(data.logs);
            } else {
                setMessage('No Activity Log for this Student');
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            setMessage('An error occurred while fetching the logs.');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.pageContainer}>
                <Loading />
            </div>
        );
    }

    return (
        <div style={styles.pageContainer}>
            {message && <span style={styles.messageLabel}>{message}</span>}
            
            {!message && logs.length > 0 && (
                <div style={styles.gridContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...styles.th, width: '15%' }}>Modified By</th>
                                <th style={{ ...styles.th, width: '15%' }}>Modified Date</th>
                                <th style={{ ...styles.th, width: '25%' }}>Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log, index) => {
                                const isAlternate = index % 2 !== 0;
                                const rowStyle = isAlternate ? styles.rowAlternate : styles.rowNormal;
                                
                                return (
                                    <tr key={index} style={rowStyle}>
                                        <td style={{...styles.td, borderLeft:'1px solid #000', borderRight:'1px solid #000'}}>{log.ModifiedBy}</td>
                                        <td style={{...styles.td, borderRight:'1px solid #000'}}>{log.ModifiedDate}</td>
                                        <td style={{...styles.td, borderRight:'1px solid #000'}}>{log.Activity}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default WLPModifiedLog;